import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import useSEO from "../hooks/useSEO";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { Reveal } from "../lib/motion";
import { POSTS, CATEGORIES, formatDate } from "../data/blog";

function PostCard({ post, i }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (i % 3) * 0.07 }}
      data-testid={`blog-card-${post.slug}`}
    >
      <Link to={`/blog/${post.slug}`} className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_20px_50px_-20px_rgba(7,17,31,0.15)]">
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="rounded-full border border-slate-200 bg-[#F8FAFC] px-3 py-1 font-medium text-[#07111F]">{post.category}</span>
          <span>{formatDate(post.date)}</span>
        </div>
        <h3 className="mt-5 font-display text-xl font-medium leading-snug tracking-tight text-[#07111F] transition-colors group-hover:text-blue-700 sm:text-2xl">
          {post.title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{post.excerpt}</p>
        <div className="mt-6 flex items-center justify-between text-sm">
          <span className="text-slate-400">{post.readTime}</span>
          <span className="inline-flex items-center gap-1 font-medium text-[#07111F]">
            Read article <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </Link>
    </motion.article>
  );
}

export default function Blog() {
  useSEO({
    title: "Insights & Blog | SefKam Digital Solutions",
    description: "Practical thinking on AI, automation, software engineering and the smart home — from the team at SefKam Digital Solutions.",
    path: "/blog",
  });
  const [cat, setCat] = useState("All");
  const [featured, ...rest] = POSTS;
  const filtered = cat === "All" ? rest : POSTS.filter((p) => p.category === cat);
  const showFeatured = cat === "All";

  return (
    <>
      <PageHero
        eyebrow="Insights"
        title="Engineering & AI, thought through."
        subtitle="Practical writing on building intelligent systems — what works in production, what doesn't, and why. No hype, no vendor pitches."
      />

      <section className="bg-[#F8FAFC] py-20 md:py-28" data-testid="blog-index">
        <div className="container-x">
          {showFeatured && (
            <Reveal>
              <Link
                to={`/blog/${featured.slug}`}
                className="group grid gap-8 rounded-[2rem] bg-[#07111F] p-8 transition-all duration-300 hover:shadow-[0_30px_70px_-25px_rgba(7,17,31,0.5)] md:grid-cols-[1fr_auto] md:items-end md:p-12 grain"
                data-testid="blog-featured"
              >
                <div>
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="rounded-full border border-white/15 px-3 py-1 font-medium text-cyan-300">Latest</span>
                    <span>{featured.category}</span>
                    <span>·</span>
                    <span>{formatDate(featured.date)}</span>
                  </div>
                  <h2 className="mt-5 max-w-2xl font-display text-3xl font-medium leading-tight tracking-tight text-white sm:text-4xl">
                    {featured.title}
                  </h2>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">{featured.excerpt}</p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-white">
                  Read article
                  <span className="grid h-10 w-10 place-items-center rounded-full border border-white/20 transition-all duration-300 group-hover:border-white/50 group-hover:bg-white group-hover:text-[#07111F]">
                    <ArrowUpRight size={16} />
                  </span>
                </span>
              </Link>
            </Reveal>
          )}

          <div className="mt-12 flex flex-wrap items-center gap-2" data-testid="blog-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  cat === c ? "bg-[#07111F] text-white" : "border border-slate-200 bg-white text-slate-500 hover:text-[#07111F]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((post, i) => (
              <PostCard key={post.slug} post={post} i={i} />
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Work with us"
        title="Want this thinking applied to your business?"
        subtitle="Tell us what you're trying to build, automate, or improve — we'll bring the engineering."
      />
    </>
  );
}
